import { useEffect, useRef, useState } from 'react';
import {
  Card,
  Table,
  Button,
  TableBody,
  Container,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  Grid,
  TextField,
  InputAdornment,
  Avatar,
  Box,
} from '@mui/material';
import { Link, Link as RouterLink } from 'react-router-dom';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import Scrollbar from '../../components/Scrollbar';
import Label from '../../components/Label';
import { useAppDispatch, useAppSelector } from '../../store';
import { findAddOnProductVariant } from '../../store/addonProductVariant/api';
import Loader from '../../components/Loader';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { encryptString, getAssetURl } from '../../utils/url';
import { uniqBy } from 'lodash';
import AutoComplete from '../../components/AutoComplete';
import {
  findSelectAddOnCategory,
  searchSelectAddOnCategory,
} from '../../store/selectAddOnCategory/api';
import { reset as resetAddOnCategory } from '../../store/selectAddOnCategory/selectAddOnCategorySlice';
import {
  findSelectAddOnProduct,
  searchSelectAddOnProduct,
} from '../../store/selectAddOnProduct/api';
import { reset as resetAddOnProduct } from '../../store/selectAddOnProduct/selectAddOnProductSlice';
import CloseIcon from '@mui/icons-material/Close';

const Index = () => {
  const dispatch = useAppDispatch();
  const { data, isLoading } = useAppSelector(
    (state) => state.addOnProductVariant
  );
  const [search, setSearch] = useState<string>('');
  const [addOnCategoryId, setAddOnCategoryId] = useState<string>('');
  const [addOnProductId, setAddOnProductId] = useState<string>('');
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );

  useEffect(() => {
    dispatch(
      findAddOnProductVariant({
        ...(search && { search: search }),
        ...(addOnCategoryId && { addonCategoryId: addOnCategoryId }),
        ...(addOnProductId && { addonProductId: addOnProductId }),
      })
    );
  }, [dispatch, search, addOnCategoryId, addOnProductId]);

  // Addon Category Data
  const {
    data: addOnCategoryData,
    isLoading: isAddOnCategoryLoading,
    status: addOnCategoryStatus,
  } = useAppSelector((state) => state.selectAddOnCategory);

  useEffect(() => {
    if (addOnCategoryStatus === 'idle') {
      dispatch(findSelectAddOnCategory({}));
    }
  }, [addOnCategoryStatus]);

  const searchAddOnCategoryStatus = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetAddOnCategory());
      return;
    }
    const params = {
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(searchSelectAddOnCategory(params));
  };

  const uniqueAddOnCategoryData = uniqBy([...addOnCategoryData], 'id');

  // Addon Product Data
  const {
    data: addOnProductData,
    isLoading: isAddOnProductLoading,
    status: addOnProductStatus,
  } = useAppSelector((state) => state.selectAddOnProduct);

  useEffect(() => {
    if (addOnProductStatus === 'idle') {
      dispatch(findSelectAddOnProduct({}));
    }
  }, [addOnProductStatus]);

  const searchAddOnProduct = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetAddOnProduct());
      return;
    }
    const params = {
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(searchSelectAddOnProduct(params));
  };

  const uniqueAddOnProductData = uniqBy([...addOnProductData], 'id');

  const boxRef = useRef<HTMLDivElement>(null);
  const [openLightBox, setOpenLightBox] = useState(false);
  const [imageState, setImageState] = useState<string>();

  const handleOpenLightBox = (imageObject: string) => {
    setOpenLightBox(true);
    setImageState(imageObject);
  };

  const handleCloseLightBox = () => {
    setOpenLightBox(false);
    setImageState('');
  };

  return (
    <Page title={'Add-On Product Variant: List'}>
      <Container maxWidth={'xl'}>
        <HeaderBreadcrumbs
          heading={'Add-On Product Variant'}
          links={[{ name: 'Dashboard', href: '/' }]}
          action={
            <Button
              variant='contained'
              component={RouterLink}
              to={'/addon-product-variant/create'}
              startIcon={<Iconify icon={'eva:plus-fill'} />}
            >
              {'New Addon Product Variant'}
            </Button>
          }
        />

        <Card>
          <Grid
            container
            spacing={2}
            sx={{ py: 2.5, px: 3 }}
          >
            <Grid
              item
              xs={12}
              sm={4}
            >
              <AutoComplete
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                  } else if (newValue) {
                    setAddOnCategoryId(newValue.id);
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  if (!newInputValue) {
                    setAddOnCategoryId('');
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props}>{option.labelEn}</li>
                )}
                getOptionLabel={(option) => option.labelEn}
                fetchFunction={() => null}
                label={'Add-on Category'}
                loading={isAddOnCategoryLoading}
                options={uniqueAddOnCategoryData}
                searchFunction={searchAddOnCategoryStatus}
                value={
                  uniqueAddOnCategoryData?.filter(
                    (addOnCategory) => addOnCategory.id === addOnCategoryId
                  )?.[0] || null
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={4}
            >
              <AutoComplete
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                  } else if (newValue) {
                    setAddOnProductId(newValue.id);
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  if (!newInputValue) {
                    setAddOnProductId('');
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props}>{option.labelEn}</li>
                )}
                getOptionLabel={(option) => option.labelEn}
                fetchFunction={() => null}
                label={'Add-on Product'}
                loading={isAddOnProductLoading}
                options={uniqueAddOnProductData}
                searchFunction={searchAddOnProduct}
                value={
                  uniqueAddOnProductData?.filter(
                    (addOnProduct) => addOnProduct.id === addOnProductId
                  )?.[0] || null
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={4}
            >
              <TextField
                fullWidth
                onChange={(e) => setSearch(e.target.value)}
                placeholder={'Search...'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Iconify
                        icon={'eva:search-fill'}
                        sx={{ color: 'text.disabled', width: 20, height: 20 }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          {/* <Searchbar handleSearch={handleSearch} /> */}
          <Scrollbar>
            <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
              <Table size={'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>{'Image'}</TableCell>
                    <TableCell align='center'>{'Label EN'}</TableCell>
                    <TableCell align='center'>{'Label AR'}</TableCell>
                    <TableCell align='center'>{'Add-On Category'}</TableCell>
                    <TableCell align='center'>{'Status'}</TableCell>
                    <TableCell align='center'>{'Action'}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow hover>
                      <TableCell
                        colSpan={8}
                        align='center'
                      >
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align='center'
                      >
                        {'No record found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data?.map((addonProductVariant) => (
                      <TableRow key={addonProductVariant.id}>
                        <TableCell
                          align='center'
                          sx={{ display: 'flex', justifyContent: 'center' }}
                        >
                          {addonProductVariant?.previewUrl && (
                            <Avatar
                              onClick={() =>
                                handleOpenLightBox(
                                  addonProductVariant?.previewUrl
                                )
                              }
                              alt={addonProductVariant?.labelEn || 'addon'}
                              src={getAssetURl(
                                addonProductVariant?.previewUrl,
                                assetUrl
                              )}
                              sx={{
                                borderRadius: 'unset',
                                m: 1,
                                objectFit: 'contain',
                                '& img': { objectFit: 'contain' },
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          {addonProductVariant?.labelEn}
                        </TableCell>
                        <TableCell align='center'>
                          {addonProductVariant?.labelAr}
                        </TableCell>
                        <TableCell align='center'>
                          {addonProductVariant?.addon_category?.labelEn}
                        </TableCell>
                        <TableCell align='center'>
                          <Label
                            variant='ghost'
                            color={
                              addonProductVariant.activeState
                                ? 'success'
                                : 'error'
                            }
                          >
                            {addonProductVariant.activeState
                              ? 'Active'
                              : 'InActive'}
                          </Label>
                        </TableCell>
                        <TableCell align='center'>
                          <IconButton aria-label='visibility'>
                            <Link
                              to={`/addon-product-variant/edit/${encryptString(
                                addonProductVariant?.id ?? ''
                              )}`}
                              state={{ ...addonProductVariant }}
                              style={{ color: '#808080' }}
                            >
                              <Iconify icon={'eva:edit-fill'} />
                            </Link>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>
      </Container>

      {openLightBox && (
        <Box
          sx={{ pt: 'env(safe-area-inset-top) !important' }}
          id='myModal'
          className='modal'
        >
          <span className='close cursor'>
            <CloseIcon
              fontSize='large'
              onClick={handleCloseLightBox}
              sx={{ mt: 'env(safe-area-inset-top) !important' }}
            />
          </span>
          <div
            className={`modal-content ${
              openLightBox ? 'display-block ' : 'display-none'
            }`}
          >
            {imageState && (
              <div className='vertical-center-div'>
                <div
                  ref={boxRef}
                  className={`mySlides ${
                    openLightBox ? 'display-block ' : 'display-none'
                  }`}
                >
                  <img
                    id={imageState}
                    src={getAssetURl(imageState, assetUrl)}
                    width='100%'
                    height='100%'
                    crossOrigin='anonymous'
                    alt={imageState}
                  />
                </div>
              </div>
            )}
          </div>
        </Box>
      )}
    </Page>
  );
};

export default Index;
